import { FC, useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Collapse,
  Typography,
  createStyles,
  makeStyles,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SearchIcon from '@material-ui/icons/Search';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import Button from 'src/components/Button';
import { Theme } from 'src/theme/types/createPalette';
import { getCampaignList, updateCampaignFolder } from 'src/apis/campaign';
import useAuth from 'src/hooks/useAuth';
import useCampaign from 'src/hooks/useCampaign';
import useNotification from 'src/hooks/useNotification';
import { CampaignProps } from 'src/types/campaign';

import { CampaignItem } from './CampaignItem';
import { CampaignFolder } from './CampaignFolder';

const UNCATEGORIZED_DROPPABLE_ID = 'Uncategorized Campaigns';

export const useStyles: any = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      height: 'calc(100vh - 50px)',
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      maxHeight: '100%',
      overflow: 'auto',
    },
    addBtn: {
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #000000',
      fontWeight: 'normal',
      fontSize: 16,
      '&.MuiButton-containedPrimary:hover': {
        backgroundColor: '#5EA1F2',
        color: '#fff',
      },
    },
    searchBar: {
      width: '100%',
      border: '1px solid #C3C3C7',
      padding: '2px 8px',
      borderRadius: 36,

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    collapseContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: '20px 12px',
      paddingRight: 0,
    },
    collapseTitle: {
      fontSize: 15,
      fontWeight: 500,
      flex: 1,
    },
    collapseCounter: {
      border: '1px solid #000000',
      borderRadius: 2,
      fontSize: 12,
      lineHeight: '12px',
      padding: 4,
      marginRight: 8,
    },
  })
);

interface CampaignListSidebarProps {}

const CampaignListSidebar: FC<CampaignListSidebarProps> = () => {
  const classes = useStyles();
  const {
    campaigns,
    setCampaigns,
    selectedCampaignId,
    setSelectedCampaignId,
    setOpenCreateModal,
  } = useCampaign();
  const { user } = useAuth();
  const isViewer = user?.role === 'view';
  const { handleSuccess, handleError } = useNotification();

  const [openUncategorized, setOpenUncategorized] = useState<boolean>(true);
  const [openFolders, setOpenFolders] = useState<boolean>(true);
  const [openRecentlyViewed, setOpenRecentlyViewed] = useState<boolean>(true);
  const [uncategorizedCampaigns, setUncategorizedCampaigns] = useState<
    CampaignProps[]
  >([]);
  const [folders, setFolders] = useState<Record<string, CampaignProps[]>>({});
  const [activeCampaign, setActiveCampaign] = useState<CampaignProps | null>(
    null
  );
  const [searchText, setSearchText] = useState<string>('');

  const fetchCampaigns = useCallback(async () => {
    const response = await getCampaignList({
      page: 0,
      pageSize: 500,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    });
    setCampaigns(response.campaigns);
  }, [setCampaigns]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    const nextUncategorized: CampaignProps[] = [];
    const nextFolders: Record<string, CampaignProps[]> = {};
    campaigns.forEach((item: CampaignProps) => {
      const folderName = item.folder?.trim();
      if (folderName) {
        if (nextFolders[folderName]) {
          nextFolders[folderName] = [...nextFolders[folderName], item];
        } else {
          nextFolders[folderName] = [item];
        }
      } else {
        nextUncategorized.push(item);
      }
    });
    setFolders(nextFolders);
    setUncategorizedCampaigns(nextUncategorized);
  }, [campaigns]);

  useEffect(() => {
    if (selectedCampaignId) {
      const c = campaigns.find((item) => item.id === selectedCampaignId);
      setActiveCampaign(c || campaigns[0] || null);
    } else {
      setActiveCampaign(campaigns[0] || null);
    }
  }, [campaigns, selectedCampaignId]);

  const handleNewFolder = () => {
    let folderName = 'New Folder';
    for (let index = 0; index < 10000; index += 1) {
      if (folders[folderName]) {
        folderName = `New Folder ${index + 1}`;
      } else {
        break;
      }
    }
    const nextFolders: Record<string, CampaignProps[]> = {
      ...folders,
    };
    nextFolders[folderName] = [];
    setFolders(nextFolders);
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || !source) {
      return;
    }

    if (destination.droppableId === source.droppableId) {
      return;
    }

    let dragItem: CampaignProps | null = null;
    const tempUncategorized = [...uncategorizedCampaigns];
    const tempFolders = { ...folders };

    if (source.droppableId === UNCATEGORIZED_DROPPABLE_ID) {
      dragItem = tempUncategorized[source.index];
      tempUncategorized.splice(source.index, 1);
    } else {
      dragItem = folders[source.droppableId][source.index];
      const temp = [...tempFolders[source.droppableId]];
      temp.splice(source.index, 1);
      tempFolders[source.droppableId] = temp;
    }

    if (!dragItem) {
      return;
    }

    if (destination.droppableId === UNCATEGORIZED_DROPPABLE_ID) {
      tempUncategorized.push(dragItem);
    } else {
      tempFolders[destination.droppableId] = [
        ...tempFolders[destination.droppableId],
        dragItem,
      ];
    }

    const newFolderValue =
      destination.droppableId === UNCATEGORIZED_DROPPABLE_ID
        ? ''
        : destination.droppableId;

    setUncategorizedCampaigns(tempUncategorized);
    setFolders(tempFolders);

    try {
      await updateCampaignFolder(dragItem.id, newFolderValue);
      handleSuccess('Campaign folder updated');
      await fetchCampaigns();
    } catch (error) {
      handleError(error, 'Could not update campaign folder');
      await fetchCampaigns();
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    const items = folders[folderName] || [];
    const tempFolders = { ...folders };
    setUncategorizedCampaigns([...uncategorizedCampaigns, ...items]);
    delete tempFolders[folderName];
    setFolders(tempFolders);

    try {
      await Promise.all(items.map((item) => updateCampaignFolder(item.id, '')));
      handleSuccess('Folder removed');
      await fetchCampaigns();
    } catch (error) {
      handleError(error, 'Could not update campaign folders');
      await fetchCampaigns();
    }
  };

  const handleRenameFolder = async (folderName: string, newName: string) => {
    const items = folders[folderName] || [];
    const updates = items.map((item) => updateCampaignFolder(item.id, newName));
    const tempFolders = { ...folders };
    tempFolders[newName] = tempFolders[folderName];
    delete tempFolders[folderName];
    setFolders(tempFolders);

    if (updates.length) {
      try {
        await Promise.all(updates);
        handleSuccess('Folder renamed');
        await fetchCampaigns();
      } catch (error) {
        handleError(error, 'Could not rename folder');
        await fetchCampaigns();
      }
    }
  };

  const handleChangeSearchText = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const searchLower = searchText.trim().toLowerCase();
  const matchesSearch = (c: CampaignProps) =>
    c.name?.toLowerCase().includes(searchLower);

  return (
    <div className={classes.container}>
      <Typography
        variant='h5'
        style={{
          color: '#000',
          fontSize: 20,
          lineHeight: '24px',
          fontWeight: 500,
        }}
      >
        All Campaigns
      </Typography>
      <TextField
        InputLabelProps={{
          shrink: true,
          style: { fontWeight: 'normal' },
        }}
        InputProps={{
          placeholder: 'Search all campaigns...',
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon htmlColor='#858591' style={{ fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        value={searchText}
        onChange={handleChangeSearchText}
        className={classes.searchBar}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.mainContent}>
          <Grid container>
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              onClick={() => {
                setOpenRecentlyViewed(!openRecentlyViewed);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography className={classes.collapseTitle}>
                Recently Viewed
              </Typography>
              {openRecentlyViewed ? (
                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
              )}
            </Grid>
            <Collapse
              in={openRecentlyViewed}
              timeout='auto'
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Grid container className={classes.collapseContainer}>
                {activeCampaign && (
                  <CampaignItem
                    key={activeCampaign.id}
                    index={0}
                    campaign={activeCampaign}
                    noDraggable
                    onSelectCampaign={setSelectedCampaignId}
                  />
                )}
              </Grid>
            </Collapse>
          </Grid>
          <Grid container>
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              onClick={() => {
                setOpenFolders(!openFolders);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography className={classes.collapseTitle}>Folders</Typography>
              <Typography className={classes.collapseCounter}>
                {Object.keys(folders).length}
              </Typography>
              {openFolders ? (
                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
              )}
            </Grid>
            <Collapse
              in={openFolders}
              timeout='auto'
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Grid container className={classes.collapseContainer}>
                {Object.keys(folders).map((key) => (
                  <CampaignFolder
                    key={key}
                    name={key}
                    campaigns={folders[key].filter(matchesSearch)}
                    onDeleteFolder={handleDeleteFolder}
                    onRenameFolder={handleRenameFolder}
                    onSelectCampaign={setSelectedCampaignId}
                  />
                ))}
              </Grid>
            </Collapse>
          </Grid>
          <Droppable droppableId={UNCATEGORIZED_DROPPABLE_ID}>
            {(provided) => (
              <Grid
                container
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <Grid
                  container
                  item
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  onClick={() => {
                    setOpenUncategorized(!openUncategorized);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography className={classes.collapseTitle}>
                    Uncategorized Campaigns
                  </Typography>
                  <Typography className={classes.collapseCounter}>
                    {uncategorizedCampaigns.filter(matchesSearch).length}
                  </Typography>
                  {openUncategorized ? (
                    <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                  )}
                </Grid>
                <Collapse
                  in={openUncategorized}
                  timeout='auto'
                  unmountOnExit
                  style={{ width: '100%' }}
                >
                  <Grid container className={classes.collapseContainer}>
                    {uncategorizedCampaigns
                      .filter(matchesSearch)
                      .map((campaign, index) => (
                        <CampaignItem
                          key={campaign.id}
                          index={index}
                          campaign={campaign}
                          onSelectCampaign={setSelectedCampaignId}
                        />
                      ))}
                  </Grid>
                  {provided.placeholder}
                </Collapse>
              </Grid>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      {!isViewer && (
        <>
          <Button
            title='+ Add new folder'
            color='primary'
            fontSize='sm'
            height={36}
            width='100%'
            className={classes.addBtn}
            onClick={handleNewFolder}
          />
          <Button
            title='+ New Campaign'
            color='primary'
            fontSize='sm'
            height={36}
            width='100%'
            className={classes.addBtn}
            onClick={() => setOpenCreateModal(true)}
          />
        </>
      )}
    </div>
  );
};

export default CampaignListSidebar;
